import React, {useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {useSelector} from "react-redux";
import moment from "moment";

import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import VLFCard from "@/components/Card";
import VLFTextField from "@/components/Input/TextField";
import VLFDatePicker from "@/components/Input/DatePicker";
import VLFSelectNew from "@/components/Input/SelectNew";

import {StaffAPI} from "@/api/staff";
import {RosterAPI} from "@/api/roster";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";

import {
	REQUIRED_FIELD,
	SUCCESSFUL_OPERATION,
	ERROR_OPERATION,
	getInternalClubId,
} from "@/utils/constants";
import {
	checkRequiredFields,
	getRosterName,
	visibleRosters,
} from "@/utils/functions";

import {useStaffRoles} from "@/utils/hooks";

import "./index.scss";
import {useTranslation} from "react-i18next";

const ASSIGNMENT_OPTIONS = [
	{value: "pool", label: "Staff del club (senza squadra)"},
	{value: "roster", label: "Assegna a una rosa"},
];

const ManagerHandleStaffPage = (props) => {
	const {edit, handleNotification} = props;

	const navigate = useNavigate();
	const {id} = useParams();
	const {t} = useTranslation();

	const userId = useSelector((state) => state?.user?.userId);
	const userName = useSelector((state) => state?.user?.userName);
	const seasonId = useSelector((state) => state?.season?.id);

	const {staffRoles} = useStaffRoles({
		setLoading: false,
		handleNotification,
	});

	const [loading, setLoading] = useState(false);
	const [errors, setErrors] = useState([]);
	const [fields, setFields] = useState({
		dateOfBirth: null,
		unit: null,
		role: null,
		categories: null,
		assignment: ASSIGNMENT_OPTIONS[0],
		roster: null,
	});

	const [rosters, setRosters] = useState([]);
	const loadRosters = async () => {
		try {
			const response = await RosterAPI.list({
				seasonId,
				prospect: false,
				clubId: getInternalClubId(),
			});
			setRosters(
				visibleRosters(
					(response || []).filter(
						(r) =>
							!r.prospect && (!r.clubId || r.clubId === getInternalClubId()),
					),
				).map((r) => ({value: r.id, label: getRosterName(r)})),
			);
		} catch {
			setRosters([]);
		}
	};

	useEffect(() => {
		if (!edit && seasonId) loadRosters();
	}, [edit, seasonId]); // eslint-disable-line react-hooks/exhaustive-deps

	const [staffUnits, setStaffUnits] = useState([]);
	const getStaffUnits = async () => {
		try {
			const response = await StaffAPI.unitsList();
			setStaffUnits(response || []);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		}
	};
	useEffect(() => {
		getStaffUnits();
	}, []);

	const getStaff = async () => {
		setLoading(true);
		try {
			const response = await StaffAPI.getById(id);
			response.unit = response.unit
				? {value: response.unit.id, label: response.unit.name}
				: null;
			response.role = response.role
				? {value: response.role.id, label: response.role.name}
				: null;
			setFields(response);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (edit) {
			getStaff();
		}
	}, [edit]);

	const getRequiredFields = () => {
		const base = ["firstName", "lastName", "unit", "role"];
		if (!edit && fields?.assignment?.value === "roster") base.push("roster");
		return base;
	};

	const saveStaff = async () => {
		try {
			const errors = checkRequiredFields(getRequiredFields(), fields);
			setErrors(errors);

			if (!errors.length) {
				setLoading(true);

				let body = Object.assign({}, fields);

				body = {
					...fields,
					dateOfBirth: fields.dateOfBirth
						? moment(fields.dateOfBirth).format("YYYY-MM-DD")
						: undefined,
					role: {id: fields.role.value, name: fields.role.label},
					staffRoleId: fields.role.value,
					unit: {id: fields.unit.value, name: fields.unit.label},
					staffUnitId: fields.unit.value,
					categories: fields?.categories?.map((c) => {
						return {
							playersCategoryId: c.value,
							playersCategory: c.label,
						};
					}),
					userId,
					user: {userId, userName},
				};

				delete body.assignment;
				delete body.roster;

				if (!edit) {
					const assignment = fields?.assignment?.value;
					if (assignment === "roster") {
						body.rosterId = fields?.roster?.value;
					} else if (assignment === "pool") {
						body.addToPool = true;
					}
				}

				const response = edit
					? await StaffAPI.update(id, body)
					: await StaffAPI.create(body);

				setTimeout(() => {
					navigate(`/manager/staffs/${response?.id || id}`);
					setLoading(false);
					handleNotification(SUCCESSFUL_OPERATION, "success");
				}, 1000);
			}
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		}
	};

	const handleSubmit = (event) => {
		event.preventDefault();
		saveStaff();
	};

	const getTextField = (
		fieldKey,
		label,
		required,
		values = fields,
		setValues = setFields,
	) => {
		return (
			<VLFTextField
				value={values[fieldKey] || ""}
				onChange={(e) => {
					required && setErrors([]);
					setValues({...values, [fieldKey]: e.target.value});
				}}
				label={label}
				placeholder={label}
				required={required}
				size="small"
				helperText={errors.indexOf(fieldKey) !== -1 ? REQUIRED_FIELD : ""}
				error={errors.indexOf(fieldKey) !== -1}
			/>
		);
	};

	const goBack = () => {
		if (edit) {
			navigate("/manager/staffs/" + id);
		} else {
			navigate("/manager/staffs");
		}
	};

	const mainActions = [
		{
			icon: <ArrowBackIcon />,
			title: "Indietro",
			onClick: goBack,
			color: "info",
		},
		{
			icon: <SaveIcon />,
			title: "Salva",
			onClick: handleSubmit,
			disabled: checkRequiredFields(getRequiredFields(), fields).length
				? true
				: false,
		},
	];

	const breadcrumbsElements = [{label: "Staff", path: "/manager/staffs"}];
	if (edit) {
		breadcrumbsElements.push(
			{label: "/"},
			{
				label: fields
					? (fields.firstName || "") + " " + (fields.lastName || "")
					: "",
				path: "/manager/staffs/" + id,
			},
		);
	}

	return (
		<Page>
			<Loading visible={loading} />
			<Breadcrumbs
				elements={breadcrumbsElements}
				current={edit ? "Modifica" : "Nuovo membro dello staff"}
				actions={<MainActions actions={mainActions} />}
			/>
			<div className="sheet-container">
				<div className="row no-margin">
					<div className="col-sm-12 padding-small">
						<VLFCard
							title="Informazioni principali"
							content={
								<div className="row">
									<div className="col-sm-12 col-md-6">
										{getTextField("firstName", "Nome", true)}
									</div>
									<div className="col-sm-12 col-md-6">
										{getTextField("lastName", "Cognome", true)}
									</div>
									<div className="col-sm-12 col-md-6">
										<VLFDatePicker
											label={t("birthdate")}
											value={fields.dateOfBirth}
											onChange={(value) => {
												setErrors([]);
												setFields({...fields, dateOfBirth: value});
											}}
											size="small"
										/>
									</div>
									<div className="col-sm-12 col-md-6">
										<VLFSelectNew
											options={staffUnits.map((su) => {
												return {
													value: su.id,
													label: su.name,
												};
											})}
											value={fields.unit}
											onChange={(newValue) => {
												setErrors([]);
												setFields({...fields, unit: newValue});
											}}
											label="Area"
											required
											helperText={
												errors.indexOf("unit") !== -1 ? REQUIRED_FIELD : ""
											}
											error={errors.indexOf("unit") !== -1}
										/>
									</div>
									<div className="col-sm-12 col-md-6">
										<VLFSelectNew
											options={staffRoles.map((sr) => {
												return {
													value: sr.id,
													label: sr.name,
												};
											})}
											value={fields.role}
											onChange={(newValue) => {
												setErrors([]);
												setFields({...fields, role: newValue});
											}}
											label="Ruolo"
											required
											helperText={
												errors.indexOf("role") !== -1 ? REQUIRED_FIELD : ""
											}
											error={errors.indexOf("role") !== -1}
										/>
									</div>
								</div>
							}
							openable={false}
						/>
					</div>
				</div>
				{!edit && (
					<div className="row no-margin mt-2">
						<div className="col-sm-12 padding-small">
							<VLFCard
								title="Assegnazione"
								openable={false}
								content={
									<div className="row no-margin">
										<div className="col-sm-12 col-md-6">
											<VLFSelectNew
												options={ASSIGNMENT_OPTIONS}
												sorted={false}
												value={fields.assignment}
												onChange={(newValue) => {
													setErrors([]);
													setFields({
														...fields,
														assignment: newValue,
														roster:
															newValue?.value === "roster"
																? fields.roster
																: null,
													});
												}}
												label="Destinazione"
											/>
										</div>
										{fields?.assignment?.value === "roster" ? (
											<div className="col-sm-12 col-md-6">
												<VLFSelectNew
													options={rosters}
													sorted={false}
													value={fields.roster}
													onChange={(newValue) => {
														setErrors([]);
														setFields({...fields, roster: newValue});
													}}
													label="Rosa di destinazione"
													required
													helperText={
														errors.indexOf("roster") !== -1
															? REQUIRED_FIELD
															: ""
													}
													error={errors.indexOf("roster") !== -1}
												/>
											</div>
										) : null}
									</div>
								}
							/>
						</div>
					</div>
				)}
			</div>
		</Page>
	);
};

export default ManagerHandleStaffPage;
